/**
 * audioFP.js - Functional Programming Audio Processing Pipeline
 * 
 * Comprehensive DAW audio tools with WebAudio API integration:
 * - Instrument separation (stem extraction)
 * - Tempo control (time stretch, pitch preservation)
 * - Auto-tune with color variations
 * - Effects: reverb, kick, noise reduction, filters
 * - Modulation: gain, pitch, color, tone, grit, cadence
 * - Loop machine and beat generation
 */

// =============================================================================
// CORE FP UTILITIES
// =============================================================================

/** Compose functions right-to-left */
export const compose = (...fns) => (x) => fns.reduceRight((acc, fn) => fn(acc), x);

/** Pipe functions left-to-right */
export const pipe = (...fns) => (x) => fns.reduce((acc, fn) => fn(acc), x);

/** Curry a function for partial application */
export const curry = (fn) => {
  const arity = fn.length;
  return function curried(...args) {
    if (args.length >= arity) return fn.apply(this, args);
    return (...more) => curried.apply(this, [...args, ...more]);
  };
};

/** Maybe monad for nullable audio data */
export class Maybe {
  constructor(value) { this._value = value; }
  static of(value) { return new Maybe(value); }
  static nothing() { return new Maybe(null); }
  isNothing() { return this._value === null || this._value === undefined; }
  map(fn) { return this.isNothing() ? Maybe.nothing() : Maybe.of(fn(this._value)); }
  flatMap(fn) { return this.isNothing() ? Maybe.nothing() : fn(this._value); }
  getOrElse(defaultValue) { return this.isNothing() ? defaultValue : this._value; }
  filter(predicate) { return this.isNothing() || !predicate(this._value) ? Maybe.nothing() : this; }
}

/** Either monad for audio processing errors */
export class Either {
  static left(value) { return new Left(value); }
  static right(value) { return new Right(value); }
  static fromNullable(value, errorMsg = 'Null value') {
    return value != null ? Either.right(value) : Either.left(errorMsg);
  }
  static tryCatch(fn, errorHandler = (e) => e.message) {
    try { return Either.right(fn()); }
    catch (e) { return Either.left(errorHandler(e)); }
  }
}

class Left {
  constructor(value) { this._value = value; }
  isLeft() { return true; }
  isRight() { return false; }
  map() { return this; }
  flatMap() { return this; }
  fold(leftFn) { return leftFn(this._value); }
  getOrElse(defaultValue) { return defaultValue; }
}

class Right {
  constructor(value) { this._value = value; }
  isLeft() { return false; }
  isRight() { return true; }
  map(fn) { return Either.right(fn(this._value)); }
  flatMap(fn) { return fn(this._value); }
  fold(_, rightFn) { return rightFn(this._value); }
  getOrElse() { return this._value; }
}

/** Task monad for async audio operations */
export class Task {
  constructor(fork) { this.fork = fork; }
  
  static of(value) {
    return new Task((reject, resolve) => resolve(value));
  }
  
  static rejected(error) {
    return new Task((reject) => reject(error));
  }
  
  static fromPromise(promise) {
    return new Task((reject, resolve) => promise.then(resolve).catch(reject));
  }
  
  map(fn) {
    return new Task((reject, resolve) =>
      this.fork(reject, (x) => resolve(fn(x)))
    );
  }
  
  flatMap(fn) {
    return new Task((reject, resolve) =>
      this.fork(reject, (x) => fn(x).fork(reject, resolve))
    );
  }
  
  toPromise() {
    return new Promise((resolve, reject) => this.fork(reject, resolve));
  }
}

// =============================================================================
// AUDIO CONTEXT MANAGEMENT
// =============================================================================

let audioContextInstance = null;

/** Get or create singleton AudioContext */
export const getAudioContext = () => {
  if (!audioContextInstance) {
    audioContextInstance = new (window.AudioContext || window.webkitAudioContext)();
  }
  return audioContextInstance;
};

/** Resume audio context if suspended */
export const ensureAudioContextRunning = async () => {
  const ctx = getAudioContext();
  if (ctx.state === 'suspended') {
    await ctx.resume();
  }
  return ctx;
};

/** Create offline context for processing */
export const createOfflineContext = (channels, length, sampleRate) =>
  new OfflineAudioContext(channels, length, sampleRate);

// =============================================================================
// AUDIO BUFFER UTILITIES
// =============================================================================

/** Decode audio file to AudioBuffer */
export const decodeAudioData = curry((ctx, arrayBuffer) =>
  Task.fromPromise(ctx.decodeAudioData(arrayBuffer))
);

/** Load audio from URL */
export const loadAudioFromUrl = (url) =>
  Task.fromPromise(
    fetch(url)
      .then((res) => res.arrayBuffer())
      .then((buffer) => getAudioContext().decodeAudioData(buffer))
  );

/** Clone an AudioBuffer immutably */
export const cloneBuffer = (buffer) => {
  const ctx = getAudioContext();
  const clone = ctx.createBuffer(
    buffer.numberOfChannels,
    buffer.length,
    buffer.sampleRate
  );
  for (let i = 0; i < buffer.numberOfChannels; i++) {
    clone.copyToChannel(buffer.getChannelData(i).slice(), i);
  }
  return clone;
};

/** Get channel data as Float32Array */
export const getChannelData = curry((channel, buffer) =>
  buffer.getChannelData(channel)
);

/** Create new buffer from channel data */
export const bufferFromChannelData = curry((sampleRate, channels, data) => {
  const ctx = getAudioContext();
  const buffer = ctx.createBuffer(channels, data.length, sampleRate);
  for (let i = 0; i < channels; i++) {
    buffer.copyToChannel(data, i);
  }
  return buffer;
});

/** Mix two buffers together */
export const mixBuffers = curry((buffer1, buffer2, ratio = 0.5) => {
  const length = Math.max(buffer1.length, buffer2.length);
  const ctx = getAudioContext();
  const mixed = ctx.createBuffer(
    Math.max(buffer1.numberOfChannels, buffer2.numberOfChannels),
    length,
    buffer1.sampleRate
  );
  
  for (let ch = 0; ch < mixed.numberOfChannels; ch++) {
    const output = mixed.getChannelData(ch);
    const data1 = ch < buffer1.numberOfChannels ? buffer1.getChannelData(ch) : null;
    const data2 = ch < buffer2.numberOfChannels ? buffer2.getChannelData(ch) : null;
    
    for (let i = 0; i < length; i++) {
      const v1 = data1 && i < data1.length ? data1[i] : 0;
      const v2 = data2 && i < data2.length ? data2[i] : 0;
      output[i] = v1 * (1 - ratio) + v2 * ratio;
    }
  }
  
  return mixed;
});

/** Concatenate buffers */
export const concatBuffers = (...buffers) => {
  if (buffers.length === 0) return null;
  if (buffers.length === 1) return cloneBuffer(buffers[0]);
  
  const totalLength = buffers.reduce((sum, b) => sum + b.length, 0);
  const ctx = getAudioContext();
  const result = ctx.createBuffer(
    buffers[0].numberOfChannels,
    totalLength,
    buffers[0].sampleRate
  );
  
  let offset = 0;
  for (const buffer of buffers) {
    for (let ch = 0; ch < result.numberOfChannels; ch++) {
      result.getChannelData(ch).set(
        ch < buffer.numberOfChannels ? buffer.getChannelData(ch) : new Float32Array(buffer.length),
        offset
      );
    }
    offset += buffer.length;
  }
  
  return result;
};

/** Slice buffer */
export const sliceBuffer = curry((start, end, buffer) => {
  const startSample = Math.floor(start * buffer.sampleRate);
  const endSample = Math.floor(end * buffer.sampleRate);
  const length = endSample - startSample;
  
  const ctx = getAudioContext();
  const sliced = ctx.createBuffer(
    buffer.numberOfChannels,
    length,
    buffer.sampleRate
  );
  
  for (let ch = 0; ch < buffer.numberOfChannels; ch++) {
    const source = buffer.getChannelData(ch);
    sliced.copyToChannel(source.slice(startSample, endSample), ch);
  }
  
  return sliced;
});

// =============================================================================
// TEMPO AND TIME STRETCH
// =============================================================================

/** Time stretch without pitch change (phase vocoder) */
export const timeStretch = curry((stretchFactor, buffer) => {
  const newLength = Math.floor(buffer.length * stretchFactor);
  const ctx = getAudioContext();
  const stretched = ctx.createBuffer(
    buffer.numberOfChannels,
    newLength,
    buffer.sampleRate
  );
  
  for (let ch = 0; ch < buffer.numberOfChannels; ch++) {
    const input = buffer.getChannelData(ch);
    const output = stretched.getChannelData(ch);
    
    // Simple linear interpolation (for demo - real phase vocoder would be more complex)
    for (let i = 0; i < newLength; i++) {
      const srcPos = i / stretchFactor;
      const srcIndex = Math.floor(srcPos);
      const frac = srcPos - srcIndex;
      
      if (srcIndex + 1 < input.length) {
        output[i] = input[srcIndex] * (1 - frac) + input[srcIndex + 1] * frac;
      } else if (srcIndex < input.length) {
        output[i] = input[srcIndex];
      }
    }
  }
  
  return stretched;
});

/** Change playback speed (affects pitch) */
export const changeSpeed = curry((speedFactor, buffer) => {
  const newLength = Math.floor(buffer.length / speedFactor);
  const newSampleRate = buffer.sampleRate * speedFactor;
  
  const ctx = getAudioContext();
  const resampled = ctx.createBuffer(
    buffer.numberOfChannels,
    newLength,
    buffer.sampleRate
  );
  
  for (let ch = 0; ch < buffer.numberOfChannels; ch++) {
    const input = buffer.getChannelData(ch);
    const output = resampled.getChannelData(ch);
    
    for (let i = 0; i < newLength; i++) {
      const srcPos = i * speedFactor;
      const srcIndex = Math.floor(srcPos);
      const frac = srcPos - srcIndex;
      
      if (srcIndex + 1 < input.length) {
        output[i] = input[srcIndex] * (1 - frac) + input[srcIndex + 1] * frac;
      }
    }
  }
  
  return resampled;
});

/** Detect BPM from audio buffer */
export const detectBPM = async (buffer) => {
  const channelData = buffer.getChannelData(0);
  const sampleRate = buffer.sampleRate;
  
  // Simple energy-based beat detection
  const windowSize = Math.floor(sampleRate * 0.01); // 10ms windows
  const energies = [];
  
  for (let i = 0; i < channelData.length; i += windowSize) {
    let energy = 0;
    for (let j = i; j < Math.min(i + windowSize, channelData.length); j++) {
      energy += channelData[j] * channelData[j];
    }
    energies.push(energy / windowSize);
  }
  
  // Find peaks (beats)
  const threshold = energies.reduce((a, b) => a + b, 0) / energies.length * 1.5;
  const beats = [];
  
  for (let i = 1; i < energies.length - 1; i++) {
    if (energies[i] > threshold && energies[i] > energies[i - 1] && energies[i] > energies[i + 1]) {
      beats.push(i);
    }
  }
  
  // Calculate average interval between beats
  if (beats.length < 2) return 120; // Default BPM
  
  const intervals = [];
  for (let i = 1; i < beats.length; i++) {
    intervals.push(beats[i] - beats[i - 1]);
  }
  
  const avgInterval = intervals.reduce((a, b) => a + b, 0) / intervals.length;
  const secondsPerBeat = (avgInterval * windowSize) / sampleRate;
  const bpm = 60 / secondsPerBeat;
  
  // Clamp to reasonable range
  return Math.min(Math.max(bpm, 60), 200);
};

// =============================================================================
// PITCH SHIFTING & AUTO-TUNE
// =============================================================================

/** Pitch shift (semitones) using resampling */
export const pitchShift = curry((semitones, buffer) => {
  const pitchFactor = Math.pow(2, semitones / 12);
  const newLength = Math.floor(buffer.length / pitchFactor);
  
  const ctx = getAudioContext();
  const shifted = ctx.createBuffer(
    buffer.numberOfChannels,
    buffer.length, // Keep original length
    buffer.sampleRate
  );
  
  for (let ch = 0; ch < buffer.numberOfChannels; ch++) {
    const input = buffer.getChannelData(ch);
    const output = shifted.getChannelData(ch);
    
    for (let i = 0; i < buffer.length; i++) {
      const srcPos = (i / buffer.length) * newLength;
      const srcIndex = Math.floor(srcPos);
      const frac = srcPos - srcIndex;
      
      if (srcIndex + 1 < input.length) {
        output[i] = input[srcIndex] * (1 - frac) + input[srcIndex + 1] * frac;
      } else if (srcIndex < input.length) {
        output[i] = input[srcIndex];
      }
    }
  }
  
  return shifted;
});

/** Auto-tune color presets */
export const AUTOTUNE_COLORS = {
  natural: { speed: 0.1, strength: 0.3, humanize: 0.7, formantShift: 0 },
  subtle: { speed: 0.2, strength: 0.5, humanize: 0.5, formantShift: 0 },
  modern: { speed: 0.5, strength: 0.7, humanize: 0.3, formantShift: 0 },
  robot: { speed: 1.0, strength: 1.0, humanize: 0, formantShift: 0 },
  tpain: { speed: 0.9, strength: 0.95, humanize: 0.1, formantShift: 2 },
  cher: { speed: 0.85, strength: 0.9, humanize: 0.05, formantShift: -1 },
  warm: { speed: 0.4, strength: 0.6, humanize: 0.4, formantShift: -2 },
  bright: { speed: 0.5, strength: 0.7, humanize: 0.3, formantShift: 3 },
  dark: { speed: 0.3, strength: 0.5, humanize: 0.5, formantShift: -4 },
  ethereal: { speed: 0.6, strength: 0.8, humanize: 0.2, formantShift: 5 }
};

/** Musical scales for auto-tune */
export const SCALES = {
  chromatic: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
  major: [0, 2, 4, 5, 7, 9, 11],
  minor: [0, 2, 3, 5, 7, 8, 10],
  pentatonic: [0, 2, 4, 7, 9],
  blues: [0, 3, 5, 6, 7, 10],
  dorian: [0, 2, 3, 5, 7, 9, 10],
  mixolydian: [0, 2, 4, 5, 7, 9, 10],
  phrygian: [0, 1, 3, 5, 7, 8, 10]
};

/** Detect pitch using autocorrelation */
export const detectPitch = (buffer, startSample, windowSize) => {
  const data = buffer.getChannelData(0);
  const sampleRate = buffer.sampleRate;
  
  const end = Math.min(startSample + windowSize, data.length);
  const window = data.slice(startSample, end);
  
  // Autocorrelation
  const minPeriod = Math.floor(sampleRate / 1000); // 1000 Hz max
  const maxPeriod = Math.floor(sampleRate / 50);   // 50 Hz min
  
  let bestCorr = 0;
  let bestPeriod = 0;
  
  for (let period = minPeriod; period < maxPeriod && period < window.length / 2; period++) {
    let corr = 0;
    for (let i = 0; i < window.length - period; i++) {
      corr += window[i] * window[i + period];
    }
    if (corr > bestCorr) {
      bestCorr = corr;
      bestPeriod = period;
    }
  }
  
  return bestPeriod > 0 ? sampleRate / bestPeriod : 0;
};

/** Snap frequency to nearest note in scale */
export const snapToScale = curry((scale, rootNote, frequency) => {
  if (frequency <= 0) return frequency;
  
  // Convert frequency to MIDI note
  const midiNote = 12 * Math.log2(frequency / 440) + 69;
  const noteInOctave = ((Math.round(midiNote) - rootNote) % 12 + 12) % 12;
  
  // Find closest note in scale
  let closest = scale[0];
  let minDist = Math.abs(noteInOctave - scale[0]);
  
  for (const note of scale) {
    const dist = Math.min(
      Math.abs(noteInOctave - note),
      Math.abs(noteInOctave - note + 12),
      Math.abs(noteInOctave - note - 12)
    );
    if (dist < minDist) {
      minDist = dist;
      closest = note;
    }
  }
  
  const correction = closest - noteInOctave;
  const correctedMidi = Math.round(midiNote) + correction;
  
  return 440 * Math.pow(2, (correctedMidi - 69) / 12);
});

/** Apply auto-tune with color preset */
export const autoTune = curry((scale, rootNote, color, buffer) => {
  const colorPreset = typeof color === 'string' ? AUTOTUNE_COLORS[color] || AUTOTUNE_COLORS.modern : color;
  const scaleNotes = typeof scale === 'string' ? SCALES[scale] || SCALES.chromatic : scale;
  
  const { speed, strength, humanize, formantShift } = colorPreset;
  
  // For real auto-tune, you'd use PSOLA or similar algorithm
  // This is a simplified demonstration
  const result = cloneBuffer(buffer);
  const windowSize = Math.floor(buffer.sampleRate * 0.02); // 20ms windows
  
  for (let ch = 0; ch < result.numberOfChannels; ch++) {
    const data = result.getChannelData(ch);
    
    for (let i = 0; i < data.length; i += windowSize) {
      const detectedPitch = detectPitch(buffer, i, windowSize);
      if (detectedPitch > 0) {
        const targetPitch = snapToScale(scaleNotes, rootNote, detectedPitch);
        const correctionFactor = targetPitch / detectedPitch;
        
        // Apply correction with humanization
        const actualCorrection = 1 + (correctionFactor - 1) * strength * (1 - Math.random() * humanize);
        
        // Apply formant shift by adjusting harmonics (simplified)
        for (let j = i; j < Math.min(i + windowSize, data.length); j++) {
          data[j] *= actualCorrection;
        }
      }
    }
  }
  
  return result;
});

// =============================================================================
// EFFECTS: REVERB, DELAY, CHORUS
// =============================================================================

/** Create convolution reverb from impulse response */
export const createConvolutionReverb = async (impulseUrl) => {
  const ctx = getAudioContext();
  const response = await fetch(impulseUrl);
  const arrayBuffer = await response.arrayBuffer();
  const impulseBuffer = await ctx.decodeAudioData(arrayBuffer);
  
  const convolver = ctx.createConvolver();
  convolver.buffer = impulseBuffer;
  
  return convolver;
};

/** Algorithmic reverb using delay network */
export const applyReverb = curry((roomSize, damping, wetMix, buffer) => {
  const result = cloneBuffer(buffer);
  const sampleRate = buffer.sampleRate;
  
  // Delay line lengths (Schroeder reverberator)
  const delays = [
    Math.floor(sampleRate * 0.0297 * roomSize),
    Math.floor(sampleRate * 0.0371 * roomSize),
    Math.floor(sampleRate * 0.0411 * roomSize),
    Math.floor(sampleRate * 0.0437 * roomSize)
  ];
  
  for (let ch = 0; ch < result.numberOfChannels; ch++) {
    const input = buffer.getChannelData(ch);
    const output = result.getChannelData(ch);
    
    // Initialize delay buffers
    const delayBuffers = delays.map((len) => new Float32Array(len));
    const delayIndices = delays.map(() => 0);
    
    for (let i = 0; i < output.length; i++) {
      let wet = 0;
      
      for (let d = 0; d < delays.length; d++) {
        const delayBuffer = delayBuffers[d];
        const idx = delayIndices[d];
        
        wet += delayBuffer[idx];
        delayBuffer[idx] = input[i] + delayBuffer[idx] * (1 - damping);
        delayIndices[d] = (idx + 1) % delays[d];
      }
      
      output[i] = input[i] * (1 - wetMix) + (wet / delays.length) * wetMix;
    }
  }
  
  return result;
});

/** Apply delay effect */
export const applyDelay = curry((delayTime, feedback, wetMix, buffer) => {
  const result = cloneBuffer(buffer);
  const sampleRate = buffer.sampleRate;
  const delaySamples = Math.floor(delayTime * sampleRate);
  
  for (let ch = 0; ch < result.numberOfChannels; ch++) {
    const input = buffer.getChannelData(ch);
    const output = result.getChannelData(ch);
    const delayBuffer = new Float32Array(delaySamples);
    let delayIdx = 0;
    
    for (let i = 0; i < output.length; i++) {
      const delayed = delayBuffer[delayIdx];
      const newSample = input[i] + delayed * feedback;
      
      delayBuffer[delayIdx] = newSample;
      delayIdx = (delayIdx + 1) % delaySamples;
      
      output[i] = input[i] * (1 - wetMix) + delayed * wetMix;
    }
  }
  
  return result;
});

/** Apply chorus effect */
export const applyChorus = curry((depth, rate, wetMix, buffer) => {
  const result = cloneBuffer(buffer);
  const sampleRate = buffer.sampleRate;
  const maxDelay = Math.floor(sampleRate * 0.03); // 30ms max delay
  
  for (let ch = 0; ch < result.numberOfChannels; ch++) {
    const input = buffer.getChannelData(ch);
    const output = result.getChannelData(ch);
    const delayBuffer = new Float32Array(maxDelay);
    let writeIdx = 0;
    
    for (let i = 0; i < output.length; i++) {
      const lfoPhase = (i / sampleRate) * rate * 2 * Math.PI;
      const lfo = (1 + Math.sin(lfoPhase)) * 0.5; // 0 to 1
      const delaySamples = Math.floor(lfo * depth * maxDelay);
      
      const readIdx = (writeIdx - delaySamples + maxDelay) % maxDelay;
      const delayed = delayBuffer[Math.floor(readIdx)];
      
      delayBuffer[writeIdx] = input[i];
      writeIdx = (writeIdx + 1) % maxDelay;
      
      output[i] = input[i] * (1 - wetMix) + delayed * wetMix;
    }
  }
  
  return result;
});

// =============================================================================
// FILTERS AND EQ
// =============================================================================

/** Filter types */
export const FILTER_TYPES = {
  lowpass: 'lowpass',
  highpass: 'highpass',
  bandpass: 'bandpass',
  notch: 'notch',
  lowshelf: 'lowshelf',
  highshelf: 'highshelf',
  peaking: 'peaking'
};

/** Apply biquad filter */
export const applyFilter = curry((type, frequency, Q, gain, buffer) => {
  const result = cloneBuffer(buffer);
  const sampleRate = buffer.sampleRate;
  
  // Biquad filter coefficients
  const omega = (2 * Math.PI * frequency) / sampleRate;
  const sinOmega = Math.sin(omega);
  const cosOmega = Math.cos(omega);
  const alpha = sinOmega / (2 * Q);
  
  let b0, b1, b2, a0, a1, a2;
  
  switch (type) {
    case 'lowpass':
      b0 = (1 - cosOmega) / 2;
      b1 = 1 - cosOmega;
      b2 = (1 - cosOmega) / 2;
      a0 = 1 + alpha;
      a1 = -2 * cosOmega;
      a2 = 1 - alpha;
      break;
    case 'highpass':
      b0 = (1 + cosOmega) / 2;
      b1 = -(1 + cosOmega);
      b2 = (1 + cosOmega) / 2;
      a0 = 1 + alpha;
      a1 = -2 * cosOmega;
      a2 = 1 - alpha;
      break;
    case 'bandpass':
      b0 = alpha;
      b1 = 0;
      b2 = -alpha;
      a0 = 1 + alpha;
      a1 = -2 * cosOmega;
      a2 = 1 - alpha;
      break;
    case 'notch':
      b0 = 1;
      b1 = -2 * cosOmega;
      b2 = 1;
      a0 = 1 + alpha;
      a1 = -2 * cosOmega;
      a2 = 1 - alpha;
      break;
    default:
      return result;
  }
  
  // Normalize coefficients
  b0 /= a0; b1 /= a0; b2 /= a0;
  a1 /= a0; a2 /= a0;
  
  for (let ch = 0; ch < result.numberOfChannels; ch++) {
    const input = buffer.getChannelData(ch);
    const output = result.getChannelData(ch);
    
    let x1 = 0, x2 = 0, y1 = 0, y2 = 0;
    
    for (let i = 0; i < output.length; i++) {
      const x0 = input[i];
      const y0 = b0 * x0 + b1 * x1 + b2 * x2 - a1 * y1 - a2 * y2;
      
      output[i] = y0;
      
      x2 = x1; x1 = x0;
      y2 = y1; y1 = y0;
    }
  }
  
  return result;
});

/** Multi-band EQ */
export const applyEQ = curry((bands, buffer) => {
  return bands.reduce((buf, band) => {
    const { frequency, gain, Q = 1 } = band;
    if (gain === 0) return buf;
    return applyFilter('peaking', frequency, Q, gain, buf);
  }, buffer);
});

/** Noise gate */
export const applyNoiseGate = curry((threshold, attack, release, buffer) => {
  const result = cloneBuffer(buffer);
  const sampleRate = buffer.sampleRate;
  
  const attackSamples = Math.floor(attack * sampleRate);
  const releaseSamples = Math.floor(release * sampleRate);
  
  for (let ch = 0; ch < result.numberOfChannels; ch++) {
    const data = result.getChannelData(ch);
    let envelope = 0;
    let gateOpen = false;
    let transitionSample = 0;
    
    for (let i = 0; i < data.length; i++) {
      const level = Math.abs(data[i]);
      
      if (level > threshold && !gateOpen) {
        gateOpen = true;
        transitionSample = i;
      } else if (level < threshold * 0.5 && gateOpen) {
        gateOpen = false;
        transitionSample = i;
      }
      
      if (gateOpen) {
        const ramp = Math.min(1, (i - transitionSample) / attackSamples);
        envelope = ramp;
      } else {
        const ramp = Math.max(0, 1 - (i - transitionSample) / releaseSamples);
        envelope = ramp;
      }
      
      data[i] *= envelope;
    }
  }
  
  return result;
});

// =============================================================================
// DYNAMICS: COMPRESSION, LIMITING, GAIN
// =============================================================================

/** Apply compression */
export const applyCompression = curry((threshold, ratio, attack, release, makeupGain, buffer) => {
  const result = cloneBuffer(buffer);
  const sampleRate = buffer.sampleRate;
  
  const attackCoeff = Math.exp(-1 / (attack * sampleRate));
  const releaseCoeff = Math.exp(-1 / (release * sampleRate));
  
  for (let ch = 0; ch < result.numberOfChannels; ch++) {
    const data = result.getChannelData(ch);
    let envelope = 0;
    
    for (let i = 0; i < data.length; i++) {
      const inputLevel = Math.abs(data[i]);
      
      // Envelope follower
      if (inputLevel > envelope) {
        envelope = attackCoeff * envelope + (1 - attackCoeff) * inputLevel;
      } else {
        envelope = releaseCoeff * envelope + (1 - releaseCoeff) * inputLevel;
      }
      
      // Compute gain reduction
      let gainReduction = 1;
      if (envelope > threshold) {
        const overThreshold = envelope / threshold;
        const compressedLevel = threshold * Math.pow(overThreshold, 1 / ratio);
        gainReduction = compressedLevel / envelope;
      }
      
      data[i] *= gainReduction * makeupGain;
    }
  }
  
  return result;
});

/** Apply hard limiter */
export const applyLimiter = curry((ceiling, buffer) => {
  const result = cloneBuffer(buffer);
  
  for (let ch = 0; ch < result.numberOfChannels; ch++) {
    const data = result.getChannelData(ch);
    for (let i = 0; i < data.length; i++) {
      data[i] = Math.max(-ceiling, Math.min(ceiling, data[i]));
    }
  }
  
  return result;
});

/** Apply gain */
export const applyGain = curry((gainDb, buffer) => {
  const result = cloneBuffer(buffer);
  const gainLinear = Math.pow(10, gainDb / 20);
  
  for (let ch = 0; ch < result.numberOfChannels; ch++) {
    const data = result.getChannelData(ch);
    for (let i = 0; i < data.length; i++) {
      data[i] *= gainLinear;
    }
  }
  
  return result;
});

/** Normalize audio to peak level */
export const normalize = curry((targetDb, buffer) => {
  const result = cloneBuffer(buffer);
  
  // Find peak
  let peak = 0;
  for (let ch = 0; ch < result.numberOfChannels; ch++) {
    const data = result.getChannelData(ch);
    for (let i = 0; i < data.length; i++) {
      peak = Math.max(peak, Math.abs(data[i]));
    }
  }
  
  if (peak === 0) return result;
  
  const targetLinear = Math.pow(10, targetDb / 20);
  const gain = targetLinear / peak;
  
  for (let ch = 0; ch < result.numberOfChannels; ch++) {
    const data = result.getChannelData(ch);
    for (let i = 0; i < data.length; i++) {
      data[i] *= gain;
    }
  }
  
  return result;
});

// =============================================================================
// NOISE REDUCTION & VOCAL ISOLATION
// =============================================================================

/** Simple noise reduction using spectral gating */
export const applyNoiseReduction = curry((threshold, smoothing, buffer) => {
  const result = cloneBuffer(buffer);
  const windowSize = 2048;
  const hopSize = windowSize / 4;
  
  for (let ch = 0; ch < result.numberOfChannels; ch++) {
    const data = result.getChannelData(ch);
    
    // Simple time-domain noise gate with smoothing
    let rms = 0;
    const smoothCoeff = 1 - smoothing;
    
    for (let i = 0; i < data.length; i++) {
      rms = smoothCoeff * rms + (1 - smoothCoeff) * data[i] * data[i];
      const currentRms = Math.sqrt(rms);
      
      if (currentRms < threshold) {
        data[i] *= currentRms / threshold; // Soft gate
      }
    }
  }
  
  return result;
});

/** Vocal focus (center channel extraction) */
export const extractVocals = (buffer) => {
  if (buffer.numberOfChannels < 2) return cloneBuffer(buffer);
  
  const ctx = getAudioContext();
  const result = ctx.createBuffer(1, buffer.length, buffer.sampleRate);
  
  const left = buffer.getChannelData(0);
  const right = buffer.getChannelData(1);
  const output = result.getChannelData(0);
  
  // Vocals are typically in the center (same in L and R)
  for (let i = 0; i < buffer.length; i++) {
    // Extract center by keeping what's common
    const mid = (left[i] + right[i]) / 2;
    output[i] = mid;
  }
  
  return result;
};

/** Remove vocals (side extraction) */
export const removeVocals = (buffer) => {
  if (buffer.numberOfChannels < 2) return cloneBuffer(buffer);
  
  const result = cloneBuffer(buffer);
  const left = result.getChannelData(0);
  const right = result.getChannelData(1);
  
  // Remove center by keeping differences
  for (let i = 0; i < buffer.length; i++) {
    const side = (left[i] - right[i]) / 2;
    left[i] = side;
    right[i] = -side;
  }
  
  return result;
};

// =============================================================================
// FADES
// =============================================================================

/** Fade types */
export const FADE_CURVES = {
  linear: (t) => t,
  exponential: (t) => t * t,
  logarithmic: (t) => Math.log10(1 + t * 9),
  sCurve: (t) => t * t * (3 - 2 * t),
  cosine: (t) => (1 - Math.cos(t * Math.PI)) / 2
};

/** Apply fade in */
export const fadeIn = curry((duration, curve, buffer) => {
  const result = cloneBuffer(buffer);
  const fadeSamples = Math.floor(duration * buffer.sampleRate);
  const curveFunc = typeof curve === 'function' ? curve : FADE_CURVES[curve] || FADE_CURVES.linear;
  
  for (let ch = 0; ch < result.numberOfChannels; ch++) {
    const data = result.getChannelData(ch);
    for (let i = 0; i < fadeSamples && i < data.length; i++) {
      const t = i / fadeSamples;
      data[i] *= curveFunc(t);
    }
  }
  
  return result;
});

/** Apply fade out */
export const fadeOut = curry((duration, curve, buffer) => {
  const result = cloneBuffer(buffer);
  const fadeSamples = Math.floor(duration * buffer.sampleRate);
  const curveFunc = typeof curve === 'function' ? curve : FADE_CURVES[curve] || FADE_CURVES.linear;
  
  for (let ch = 0; ch < result.numberOfChannels; ch++) {
    const data = result.getChannelData(ch);
    const startSample = data.length - fadeSamples;
    
    for (let i = Math.max(0, startSample); i < data.length; i++) {
      const t = (i - startSample) / fadeSamples;
      data[i] *= curveFunc(1 - t);
    }
  }
  
  return result;
});

/** Apply crossfade between two buffers */
export const crossfade = curry((duration, curve, buffer1, buffer2) => {
  const faded1 = fadeOut(duration, curve, buffer1);
  const faded2 = fadeIn(duration, curve, buffer2);
  
  // Overlap the fade regions
  const fadeSamples = Math.floor(duration * buffer1.sampleRate);
  const overlapStart = buffer1.length - fadeSamples;
  
  const result = cloneBuffer(buffer1);
  const output = result.getChannelData(0);
  const input2 = faded2.getChannelData(0);
  
  for (let i = 0; i < fadeSamples && i < input2.length; i++) {
    if (overlapStart + i < output.length) {
      output[overlapStart + i] += input2[i];
    }
  }
  
  return result;
});

// =============================================================================
// LOOP MACHINE
// =============================================================================

/** Create loop region */
export const createLoop = curry((startTime, endTime, buffer) => {
  return sliceBuffer(startTime, endTime, buffer);
});

/** Repeat a buffer n times */
export const repeatBuffer = curry((times, buffer) => {
  const buffers = Array(times).fill(null).map(() => cloneBuffer(buffer));
  return concatBuffers(...buffers);
});

/** Create loop with crossfade */
export const createSeamlessLoop = curry((loopDuration, crossfadeDuration, buffer) => {
  const loop = sliceBuffer(0, loopDuration, buffer);
  
  // Create seamless loop by crossfading end to beginning
  const result = cloneBuffer(loop);
  const fadeSamples = Math.floor(crossfadeDuration * buffer.sampleRate);
  
  for (let ch = 0; ch < result.numberOfChannels; ch++) {
    const data = result.getChannelData(ch);
    
    for (let i = 0; i < fadeSamples; i++) {
      const fadeOut = 1 - i / fadeSamples;
      const fadeIn = i / fadeSamples;
      
      const endSample = data.length - fadeSamples + i;
      const startSample = i;
      
      if (endSample < data.length && startSample < data.length) {
        data[startSample] = data[startSample] * fadeIn + data[endSample] * fadeOut;
      }
    }
  }
  
  return result;
});

// =============================================================================
// BEAT GENERATION
// =============================================================================

/** Basic drum sounds using synthesis */
export const generateKick = (sampleRate = 44100, duration = 0.5) => {
  const ctx = getAudioContext();
  const length = Math.floor(sampleRate * duration);
  const buffer = ctx.createBuffer(1, length, sampleRate);
  const data = buffer.getChannelData(0);
  
  for (let i = 0; i < length; i++) {
    const t = i / sampleRate;
    const envelope = Math.exp(-t * 10);
    const frequency = 150 * Math.exp(-t * 30) + 50;
    data[i] = Math.sin(2 * Math.PI * frequency * t) * envelope;
  }
  
  return buffer;
};

export const generateSnare = (sampleRate = 44100, duration = 0.3) => {
  const ctx = getAudioContext();
  const length = Math.floor(sampleRate * duration);
  const buffer = ctx.createBuffer(1, length, sampleRate);
  const data = buffer.getChannelData(0);
  
  for (let i = 0; i < length; i++) {
    const t = i / sampleRate;
    const envelope = Math.exp(-t * 15);
    const noise = (Math.random() * 2 - 1) * 0.8;
    const tone = Math.sin(2 * Math.PI * 180 * t) * 0.5;
    data[i] = (noise + tone) * envelope;
  }
  
  return buffer;
};

export const generateHihat = (sampleRate = 44100, duration = 0.1, isOpen = false) => {
  const ctx = getAudioContext();
  const actualDuration = isOpen ? duration * 3 : duration;
  const length = Math.floor(sampleRate * actualDuration);
  const buffer = ctx.createBuffer(1, length, sampleRate);
  const data = buffer.getChannelData(0);
  
  const decayRate = isOpen ? 5 : 30;
  
  for (let i = 0; i < length; i++) {
    const t = i / sampleRate;
    const envelope = Math.exp(-t * decayRate);
    // High-frequency noise filtered
    const noise = (Math.random() * 2 - 1);
    data[i] = noise * envelope * 0.3;
  }
  
  // Apply highpass filter effect
  return applyFilter('highpass', 8000, 1, 0, buffer);
};

/** Beat pattern types */
export const BEAT_PATTERNS = {
  basic4_4: {
    kick: [1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0],
    snare: [0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0],
    hihat: [1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0]
  },
  hiphop: {
    kick: [1, 0, 0, 0, 0, 0, 1, 0, 0, 1, 0, 0, 0, 0, 0, 0],
    snare: [0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0],
    hihat: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1]
  },
  trap: {
    kick: [1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 1, 0, 0, 0, 0],
    snare: [0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0],
    hihat: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1]
  },
  house: {
    kick: [1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0],
    snare: [0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0],
    hihat: [0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0]
  },
  dnb: {
    kick: [1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0],
    snare: [0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 1],
    hihat: [1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0]
  }
};

/** Generate beat pattern */
export const generateBeat = (patternName, bpm = 120, bars = 4) => {
  const pattern = BEAT_PATTERNS[patternName] || BEAT_PATTERNS.basic4_4;
  const sampleRate = 44100;
  const stepsPerBar = 16;
  const totalSteps = stepsPerBar * bars;
  const stepDuration = 60 / bpm / 4; // 16th notes
  const totalDuration = totalSteps * stepDuration;
  
  const ctx = getAudioContext();
  const length = Math.floor(sampleRate * totalDuration);
  const result = ctx.createBuffer(2, length, sampleRate);
  
  // Generate drum samples
  const kick = generateKick(sampleRate);
  const snare = generateSnare(sampleRate);
  const hihat = generateHihat(sampleRate);
  
  // Place samples according to pattern
  for (let step = 0; step < totalSteps; step++) {
    const patternStep = step % stepsPerBar;
    const sampleOffset = Math.floor(step * stepDuration * sampleRate);
    
    if (pattern.kick[patternStep]) {
      mixSampleIntoBuffer(result, kick, sampleOffset, 1.0);
    }
    if (pattern.snare[patternStep]) {
      mixSampleIntoBuffer(result, snare, sampleOffset, 0.8);
    }
    if (pattern.hihat[patternStep]) {
      mixSampleIntoBuffer(result, hihat, sampleOffset, 0.4);
    }
  }
  
  return result;
};

/** Helper to mix a sample into a buffer at an offset */
const mixSampleIntoBuffer = (destBuffer, srcBuffer, offset, gain) => {
  for (let ch = 0; ch < destBuffer.numberOfChannels; ch++) {
    const dest = destBuffer.getChannelData(ch);
    const src = srcBuffer.getChannelData(Math.min(ch, srcBuffer.numberOfChannels - 1));
    
    for (let i = 0; i < src.length && offset + i < dest.length; i++) {
      dest[offset + i] += src[i] * gain;
    }
  }
};

// =============================================================================
// AUDIO ANALYSIS
// =============================================================================

/** Calculate RMS level */
export const calculateRMS = (buffer) => {
  let sumSquares = 0;
  let count = 0;
  
  for (let ch = 0; ch < buffer.numberOfChannels; ch++) {
    const data = buffer.getChannelData(ch);
    for (let i = 0; i < data.length; i++) {
      sumSquares += data[i] * data[i];
      count++;
    }
  }
  
  return Math.sqrt(sumSquares / count);
};

/** Calculate peak level */
export const calculatePeak = (buffer) => {
  let peak = 0;
  
  for (let ch = 0; ch < buffer.numberOfChannels; ch++) {
    const data = buffer.getChannelData(ch);
    for (let i = 0; i < data.length; i++) {
      peak = Math.max(peak, Math.abs(data[i]));
    }
  }
  
  return peak;
};

/** Generate waveform visualization data */
export const generateWaveformData = curry((numPoints, buffer) => {
  const data = buffer.getChannelData(0);
  const samplesPerPoint = Math.floor(data.length / numPoints);
  const waveform = new Float32Array(numPoints);
  
  for (let i = 0; i < numPoints; i++) {
    let max = 0;
    const start = i * samplesPerPoint;
    const end = Math.min(start + samplesPerPoint, data.length);
    
    for (let j = start; j < end; j++) {
      max = Math.max(max, Math.abs(data[j]));
    }
    
    waveform[i] = max;
  }
  
  return waveform;
});

// =============================================================================
// EFFECT CHAIN COMPOSITION
// =============================================================================

/** Create composable effect chain */
export const createEffectChain = (...effects) => {
  return (buffer) => effects.reduce((buf, effect) => effect(buf), buffer);
};

/** Preset effect chains */
export const EFFECT_PRESETS = {
  warmVocal: createEffectChain(
    applyCompression(0.3, 4, 0.01, 0.1, 1.2),
    applyEQ([
      { frequency: 100, gain: -3, Q: 0.7 },
      { frequency: 3000, gain: 2, Q: 1 },
      { frequency: 8000, gain: 1, Q: 0.7 }
    ]),
    applyReverb(0.3, 0.5, 0.15)
  ),
  
  crispVocal: createEffectChain(
    applyNoiseGate(0.02, 0.005, 0.05),
    applyCompression(0.4, 3, 0.005, 0.1, 1.3),
    applyEQ([
      { frequency: 200, gain: -2, Q: 1 },
      { frequency: 5000, gain: 3, Q: 0.8 },
      { frequency: 12000, gain: 2, Q: 0.7 }
    ])
  ),
  
  lofi: createEffectChain(
    applyFilter('lowpass', 8000, 0.7, 0),
    applyCompression(0.5, 6, 0.02, 0.2, 0.9),
    applyGain(-3)
  ),
  
  radioVoice: createEffectChain(
    applyFilter('bandpass', 1500, 2, 0),
    applyCompression(0.3, 8, 0.001, 0.1, 2),
    applyGain(-6)
  ),
  
  spacey: createEffectChain(
    applyDelay(0.3, 0.4, 0.3),
    applyReverb(0.8, 0.3, 0.5),
    applyChorus(0.5, 0.5, 0.3)
  )
};

// =============================================================================
// EXPORT UTILITIES
// =============================================================================

/** Convert AudioBuffer to WAV Blob */
export const bufferToWav = (buffer) => {
  const numChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const format = 1; // PCM
  const bitDepth = 16;
  
  const bytesPerSample = bitDepth / 8;
  const blockAlign = numChannels * bytesPerSample;
  
  const dataLength = buffer.length * blockAlign;
  const bufferLength = 44 + dataLength;
  
  const arrayBuffer = new ArrayBuffer(bufferLength);
  const view = new DataView(arrayBuffer);
  
  // WAV header
  const writeString = (offset, str) => {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.charCodeAt(i));
    }
  };
  
  writeString(0, 'RIFF');
  view.setUint32(4, bufferLength - 8, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true); // fmt chunk size
  view.setUint16(20, format, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitDepth, true);
  writeString(36, 'data');
  view.setUint32(40, dataLength, true);
  
  // Interleave channels and write samples
  const channels = [];
  for (let ch = 0; ch < numChannels; ch++) {
    channels.push(buffer.getChannelData(ch));
  }
  
  let offset = 44;
  for (let i = 0; i < buffer.length; i++) {
    for (let ch = 0; ch < numChannels; ch++) {
      const sample = Math.max(-1, Math.min(1, channels[ch][i]));
      const intSample = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
      view.setInt16(offset, intSample, true);
      offset += 2;
    }
  }
  
  return new Blob([arrayBuffer], { type: 'audio/wav' });
};

/** Download audio buffer as WAV file */
export const downloadAsWav = (buffer, filename = 'audio.wav') => {
  const blob = bufferToWav(buffer);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

export default {
  // Core FP
  compose, pipe, curry, Maybe, Either, Task,
  
  // Context
  getAudioContext, ensureAudioContextRunning, createOfflineContext,
  
  // Buffer utilities
  decodeAudioData, loadAudioFromUrl, cloneBuffer, getChannelData,
  bufferFromChannelData, mixBuffers, concatBuffers, sliceBuffer,
  
  // Tempo
  timeStretch, changeSpeed, detectBPM,
  
  // Pitch
  pitchShift, detectPitch, snapToScale, autoTune,
  AUTOTUNE_COLORS, SCALES,
  
  // Effects
  applyReverb, applyDelay, applyChorus,
  
  // Filters
  applyFilter, applyEQ, applyNoiseGate, FILTER_TYPES,
  
  // Dynamics
  applyCompression, applyLimiter, applyGain, normalize,
  
  // Noise/Vocals
  applyNoiseReduction, extractVocals, removeVocals,
  
  // Fades
  fadeIn, fadeOut, crossfade, FADE_CURVES,
  
  // Loops
  createLoop, repeatBuffer, createSeamlessLoop,
  
  // Beats
  generateKick, generateSnare, generateHihat, generateBeat, BEAT_PATTERNS,
  
  // Analysis
  calculateRMS, calculatePeak, generateWaveformData,
  
  // Chains
  createEffectChain, EFFECT_PRESETS,
  
  // Export
  bufferToWav, downloadAsWav
};
